import { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Copy,
  FileText,
  Clock,
  Volume2,
  CheckCircle,
  XCircle,
  Loader2,
  Brain,
  Edit3,
  Save,
  Users,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { type TranscriptionData } from "@/redux/api/transcriptions.rtkq";
import { useGenerateQuestionsMutation, useUpdateQuestionAnswerMutation } from "@/redux/api/questions.rtkq";
import {
  useGetLatestSpeakerIdentificationQuery,
  useGenerateSpeakerIdentificationMutation,
  useUpdateSpeakerNamesMutation,
} from "@/redux/api/speakerIdentification.rtkq";
import { toast } from "sonner";

import TipTapEditor from "@/components/ui/shadcn-io/editor/TipTapEditor";
import type { HTMLContent, JSONContent } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { generateHTML } from "@tiptap/react";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";

import SpeakerRenameEditor from "@/components/audio-recording/SpeakerRenameEditor";
import DownloadWordButton from "@/components/pdf/DownloadWordButton_new";
import DownloadPdfButton from "@/components/pdf/DownloadPdfButton";

import { lexicalToTipTapHtml, isLexicalFormat, isTipTapFormat } from "@/utils/tiptapUtils";

interface TranscriptionViewerProps {
  transcription: TranscriptionData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const TranscriptionViewer = ({ transcription, isOpen, onOpenChange }: TranscriptionViewerProps) => {
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [editingAnswer, setEditingAnswer] = useState("");

  // TipTap state
  const [editorHtmlContent, setEditorHtmlContent] = useState<HTMLContent>("");
  const [speakerTipTapContent, setSpeakerTipTapContent] = useState<JSONContent | null>(null);

  // Speaker identification state
  const [isGeneratingSpeakers, setIsGeneratingSpeakers] = useState(false);
  const [speakerError, setSpeakerError] = useState<string>("");
  const [isEditingSpeakers, setIsEditingSpeakers] = useState(false);

  // Memoize questions key to prevent unnecessary re-renders
  const questionsKey = useMemo(() => transcription.questionsFromAi?.length || 0, [transcription.questionsFromAi?.length]);

  // Declare mutations
  const [generateQuestions, { isLoading: isGeneratingQuestions }] = useGenerateQuestionsMutation();
  const [updateQuestionAnswer, { isLoading: isUpdatingAnswer }] = useUpdateQuestionAnswerMutation();
  const [updateSpeakerNames, { isLoading: isUpdatingSpeakerNames }] = useUpdateSpeakerNamesMutation();

  // Speaker identification mutations
  const [generateSpeakerIdentification] = useGenerateSpeakerIdentificationMutation();
  const { data: latestSpeakerIdentification, refetch: refetchSpeakerIdentification } = useGetLatestSpeakerIdentificationQuery(
    { userId: transcription.userId, transcriptionId: transcription.id },
    { skip: !isOpen }
  );

  // Convert speaker data to TipTap format when identification loads
  useEffect(() => {
    if (latestSpeakerIdentification?.resultJson?.speakers) {
      const content = convertSpeakerDataToTipTapContent(
        latestSpeakerIdentification.resultJson.speakers
      );
      setSpeakerTipTapContent(content);
    }
  }, [latestSpeakerIdentification]);

  // Initialize or convert editor content
  useEffect(() => {
    const initializeContent = async () => {
      const savedState = transcription.editorState;

      if (!savedState) {
        // No saved state, use plain text
        const html = `<p>${transcription.transcription.replace(/\n/g, "</p><p>")}</p>`;
        setEditorHtmlContent(html);
        return;
      }

      // Check format and convert if needed
      if (isLexicalFormat(savedState)) {
        // Convert Lexial to TipTap HTML
        const html = lexicalToTipTapHtml(savedState as any);
        setEditorHtmlContent(html);
      } else if (isTipTapFormat(savedState)) {
        // Already TipTap format
        setEditorHtmlContent(savedState as string);
      } else {
        // Fallback: treat as plain text
        const html = `<p>${String(savedState).replace(/\n/g, "</p><p>")}</p>`;
        setEditorHtmlContent(html);
      }
    };

    if (isOpen) {
      initializeContent();
    }
  }, [transcription.id, transcription.editorState, transcription.transcription, isOpen, transcription.userId]);

  // Handle TipTap content update with auto-save
  const handleTipTapUpdate = useCallback(async (html: string) => {
    setEditorHtmlContent(html);
    // TipTapEditor handles debouncing, just update state
  }, []);

  // Reset editing state when transcription changes
  useEffect(() => {
    setEditingQuestionIndex(null);
    setEditingAnswer("");
    // Reset speaker identification state
    setIsGeneratingSpeakers(false);
    setSpeakerError("");
    setIsEditingSpeakers(false);
  }, [transcription.id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (durationInSeconds: number) => {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "processing":
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "הושלם";
      case "processing":
        return "מעבד";
      case "failed":
        return "נכשל";
      default:
        return "ממתין";
    }
  };

  const handleGenerateQuestions = async () => {
    try {
      await generateQuestions({ transcriptionId: transcription.id }).unwrap();
      toast.success("השאלות נוצרו בהצלחה");
    } catch (error) {
      console.error("Error generating questions:", error);
      toast.error("שגיאה ביצירת השאלות");
    }
  };

  const handleStartEdit = (questionIndex: number, currentAnswer: string) => {
    setEditingQuestionIndex(questionIndex);
    setEditingAnswer(currentAnswer);
  };

  const handleCancelEdit = () => {
    setEditingQuestionIndex(null);
    setEditingAnswer("");
  };

  const handleSaveAnswer = async (questionIndex: number) => {
    try {
      await updateQuestionAnswer({
        transcriptionId: transcription.id,
        userId: transcription.userId,
        questionIndex,
        answer: editingAnswer,
      }).unwrap();

      setEditingQuestionIndex(null);
      setEditingAnswer("");
      toast.success("התשובה נשמרה בהצלחה");
    } catch (error) {
      console.error("Error updating answer:", error);
      toast.error("שגיאה בשמירת התשובה");
    }
  };

  // Speaker identification handlers
  const handleGenerateSpeakerIdentification = async () => {
    if (!transcription || transcription.processing.status !== "completed") {
      toast.error("התמלול עדיין לא הושלם");
      return;
    }

    try {
      setIsGeneratingSpeakers(true);
      setSpeakerError("");

      await generateSpeakerIdentification({
        userId: transcription.userId,
        transcriptionId: transcription.id,
      }).unwrap();

      toast.success("זיהוי הדוברים הושלם בהצלחה");
      refetchSpeakerIdentification();
    } catch (error) {
      console.error("Speaker identification error:", error);
      const errorMsg = error instanceof Error ? error.message : "שגיאה לא ידועה";
      setSpeakerError(errorMsg);
      toast.error("שגיאה בזיהוי דוברים");
    } finally {
      setIsGeneratingSpeakers(false);
    }
  };

  const handleCopySpeakerText = async () => {
    if (!latestSpeakerIdentification?.resultHtml) return;

    try {
      // Copy HTML as plain text
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = latestSpeakerIdentification.resultHtml;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      await navigator.clipboard.writeText(plainText);
      toast.success("הטקסט הועתק ללוח");
    } catch {
      toast.error("שגיאה בהעתקה");
    }
  };

  const handleRetrySpeakerIdentification = async () => {
    // Simply call generate again - it will create a new document in sub-collection
    await handleGenerateSpeakerIdentification();
  };

  // Speaker rename handlers
  const handleStartEditSpeakers = () => {
    setIsEditingSpeakers(true);
  };

  const handleCancelEditSpeakers = () => {
    setIsEditingSpeakers(false);
  };

  const handleSaveSpeakerNames = async (mappings: Record<string, string>) => {
    if (!latestSpeakerIdentification?.resultJson) {
      toast.error("נתוני זיהוי דוברים לא זמינים");
      return;
    }

    try {
      await updateSpeakerNames({
        userId: transcription.userId,
        transcriptionId: transcription.id,
        speakerMappings: mappings,
      }).unwrap();

      toast.success("שמות הדוברים עודכנו בהצלחה");
      setIsEditingSpeakers(false);
      refetchSpeakerIdentification();
    } catch (error) {
      console.error("Error updating speaker names:", error);
      toast.error("שגיאה בעדכון שמות הדוברים");
    }
  };

  const convertSpeakerDataToTipTapContent = (
    speakers: Array<{ index: number; speakerId: string; speaker: string; text: string }>
  ): JSONContent => {
    const tableRows = speakers.map(({ speaker, text }) => ({
      type: "tableRow",
      content: [
        {
          type: "tableCell",
          content: [{
            type: "paragraph",
            content: [{ type: "text", text }]
          }]
        },
        {
          type: "tableCell",
          attrs: { colwidth: [100] },
          content: [{
            type: "paragraph",
            content: [{ type: "text", text: speaker, marks: [{ type: "bold" }] }]
          }]
        }
      ]
    }));

    return {
      type: "doc",
      content: [
        {
          type: "table",
          content: [
            // Header row
            {
              type: "tableRow",
              content: [
                { type: "tableCell", content: [{ type: "paragraph", content: [{ type: "text", text: "תמלול", marks: [{ type: "bold" }] }] }] },
                { type: "tableCell", attrs: { colwidth: [100] }, content: [{ type: "paragraph", content: [{ type: "text", text: "דובר", marks: [{ type: "bold" }] }] }] }
              ]
            },
            ...tableRows
          ]
        }
      ]
    };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-none flex-col rounded-none p-0 sm:h-[95vh] sm:max-h-[95vh] sm:w-[95vw] sm:max-w-[95vw] sm:rounded-lg">
        <DialogHeader className="flex-shrink-0 p-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] sm:p-6">
          <div className="flex items-center justify-between">
            <DialogTitle className="break-all text-right text-lg font-semibold text-chen-900 sm:text-xl">
              {transcription.fileName}
            </DialogTitle>
          </div>

          <DialogDescription className="text-muted-foreground text-right">צפייה ועריכה של תמלול קובץ אודיו</DialogDescription>

          {/* Metadata */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              <FileText className="w-3 h-3 ml-1" />
              תמלול
            </Badge>
            <Badge variant="secondary" className="bg-green-50 text-green-700">
              <Volume2 className="w-3 h-3 ml-1" />
              {transcription.metadata.language === "he" ? "עברית" : "English"}
            </Badge>
            <Badge variant="outline" className="text-muted-foreground">
              <Clock className="w-3 h-3 ml-1" />
              {formatDuration(transcription.metadata.duration)}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {getStatusIcon(transcription.processing.status)}
              {getStatusText(transcription.processing.status)}
            </div>
          </div>
        </DialogHeader>
        <Separator />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0">
          <Tabs defaultValue="transcription" className="flex-1 flex flex-col min-h-0">
            {/* Tabs Navigation */}
            <div className="flex-shrink-0 overflow-x-auto px-4 pt-4 sm:px-6">
              <TabsList className="grid w-full min-w-[20rem] grid-cols-3" dir="rtl">
                <TabsTrigger value="transcription" className="flex items-center gap-1 text-xs sm:gap-2 sm:text-sm">
                  <FileText className="h-4 w-4 shrink-0" />
                  תמלול
                </TabsTrigger>
                <TabsTrigger value="speaker-identification" className="flex items-center gap-1 text-xs sm:gap-2 sm:text-sm">
                  <Users className="h-4 w-4 shrink-0" />
                  <span className="truncate">זיהוי דוברים</span>
                  {latestSpeakerIdentification && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      ✓
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="questions" className="flex items-center gap-1 text-xs sm:gap-2 sm:text-sm">
                  <Brain className="h-4 w-4 shrink-0" />
                  <span className="truncate">שאלות AI</span>
                  {transcription.questionsFromAi && transcription.questionsFromAi.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {transcription.questionsFromAi.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Transcription Tab */}
            <TabsContent value="transcription" className="flex-1 flex flex-col min-h-0 mt-0">
              <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
                {/* TipTap Editor */}
                <div className="flex-1 overflow-hidden rounded-lg">
                  {transcription.processing.status === "completed" ? (
                    <TipTapEditor
                      fileName={transcription.fileName}
                      summaryHTML={editorHtmlContent}
                      onUpdateCallback={handleTipTapUpdate}
                    />
                  ) : transcription.processing.status === "processing" ? (
                    <div className="flex items-center justify-center h-32 text-muted-foreground border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>מעבד תמלול...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 h-32 text-destructive border rounded-lg px-4 text-center">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        <span>שגיאה בעיבוד התמלול</span>
                      </div>
                      {transcription.processing.error && (
                        <p className="text-sm text-destructive/90" role="alert">
                          {transcription.processing.error}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Speaker Identification Tab */}
            <TabsContent value="speaker-identification" className="flex-1 flex flex-col min-h-0 mt-0">
              <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
                {/* Determine which state to show */}
                {isGeneratingSpeakers ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-4">
                    <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-foreground">מעבד זיהוי דוברים...</h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        אנא המתן, המערכת מזהה דוברים...
                      </p>
                    </div>
                  </div>
                ) : speakerError ? (
                  // STATE 4: Error State
                  <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-4">
                    <div className="space-y-2">
                      <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                      <h3 className="text-lg font-medium text-foreground">שגיאה בזיהוי דוברים</h3>
                      <p className="text-sm text-muted-foreground max-w-md">{speakerError}</p>
                    </div>
                    <Button onClick={handleRetrySpeakerIdentification} variant="outline" className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4" />
                      נסה שוב
                    </Button>
                  </div>
                ) : latestSpeakerIdentification ? (
                  // STATE 3: Completed - Display Result
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Actions Bar */}
                    <div className="mb-4 flex-shrink-0">
                      <div className="flex items-center justify-between gap-3 px-3 py-2 border rounded-2xl">
                        {/* Left: Status & Edit Action */}
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-muted text-muted-foreground px-2 py-1 text-xs">
                            <CheckCircle className="h-3 w-3 ml-1" />
                            זיהוי דוברים הושלם
                          </Badge>
                        </div>

                        {/* Right: Export & Regenerate Actions */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopySpeakerText}
                            className="gap-1.5 h-8 px-3"
                            title="העתק ללוח"
                          >
                            <Copy className="h-3.5 w-3.5" />
                            <span>העתק</span>
                          </Button>
                          <DownloadWordButton
                            disabledFromParent={!speakerTipTapContent}
                            fileName={`${transcription.fileName}_זיהוי_דוברים`}
                            editorState={speakerTipTapContent!}
                          />
                          <DownloadPdfButton
                            disabledFromParent={!speakerTipTapContent}
                            htmlContent={speakerTipTapContent ? generateHTML(speakerTipTapContent, [StarterKit, Table, TableRow, TableCell, TableHeader]) : ""}
                            fileName={`${transcription.fileName}_זיהוי_דוברים`}
                          />
                          <div className="w-px h-4 bg-border mx-1" />
                          <Button variant="ghost" size="sm" onClick={handleStartEditSpeakers} className="gap-1.5 h-8 px-3">
                            <Edit3 className="h-3.5 w-3.5" />
                            <span>ערוך שמות</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRetrySpeakerIdentification}
                            disabled={isGeneratingSpeakers}
                            className="gap-1.5 h-8 px-3"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${isGeneratingSpeakers ? "animate-spin" : ""}`} />
                            <span>זיהוי מחדש</span>
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Speaker Rename Editor */}
                    {isEditingSpeakers && latestSpeakerIdentification.resultJson && (
                      <SpeakerRenameEditor
                        resultJson={latestSpeakerIdentification.resultJson}
                        onSave={handleSaveSpeakerNames}
                        onCancel={handleCancelEditSpeakers}
                        isLoading={isUpdatingSpeakerNames}
                      />
                    )}

                    {/* Result Display - TipTap Editor */}
                    {speakerTipTapContent && (
                      <div className="flex-1 overflow-hidden rounded-lg border border-border">
                        <TipTapEditor
                          content={speakerTipTapContent}
                          editable={false}
                          onChange={() => {}}
                          className="min-h-full"
                        />
                      </div>
                    )}

                    {/* Footer Metadata */}
                    <div className="text-xs text-muted-foreground text-right mt-4">
                      נוצר ב-{formatDate(latestSpeakerIdentification.createdAt)} •
                      {latestSpeakerIdentification.processingTimeMs &&
                        ` • זמן עיבוד: ${Math.round(latestSpeakerIdentification.processingTimeMs / 1000)} שניות`}
                    </div>
                  </div>
                ) : (
                  // STATE 1: Empty State - No Speaker Identification Yet
                  <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-4">
                    <div className="space-y-2">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto" />
                      <h3 className="text-lg font-medium text-foreground">אין זיהוי דוברים עדיין</h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        שלח את התמלול לזיהוי אוטומטי של דוברים. המערכת תזהה דוברים שונים ותסמן אותם בטקסט.
                      </p>
                    </div>
                    <Button
                      onClick={handleGenerateSpeakerIdentification}
                      disabled={transcription.processing.status !== "completed"}
                      className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white flex items-center gap-2"
                    >
                      <Users className="h-4 w-4" />
                      שלח לזיהוי דוברים
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Questions Tab */}
            <TabsContent value="questions" className="flex-1 flex flex-col min-h-0 mt-0">
              <div className="flex-1 flex flex-col min-h-0 p-6 pt-4">
                <div className="flex-1 overflow-hidden">
                  <ScrollArea className="h-full">
                    <div className="space-y-4" key={questionsKey}>
                      {transcription.questionsFromAi && transcription.questionsFromAi.length > 0 ? (
                        transcription.questionsFromAi.map((question, index) => (
                          <Card key={index} className="text-right">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium text-chen-900">שאלה {index + 1}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="text-sm text-foreground">{question.q}</div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-muted-foreground">תשובה:</label>
                                {editingQuestionIndex === index ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={editingAnswer}
                                      onChange={(e) => setEditingAnswer(e.target.value)}
                                      placeholder="הזן את התשובה כאן..."
                                      className="min-h-[80px] text-right"
                                    />
                                    <div className="flex gap-2 justify-end">
                                      <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                                        ביטול
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveAnswer(index)}
                                        disabled={isUpdatingAnswer}
                                        className="flex items-center gap-2"
                                      >
                                        {isUpdatingAnswer ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <Save className="h-3 w-3" />
                                        )}
                                        שמור
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="min-h-[80px] p-3 border rounded-lg bg-muted/50 text-sm">
                                      {question.a || <span className="text-muted-foreground italic">עדיין לא ניתנה תשובה</span>}
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleStartEdit(index, question.a)}
                                      className="flex items-center gap-2"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                      ערוך תשובה
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-4">
                          <div className="space-y-2">
                            <Brain className="h-12 w-12 text-muted-foreground mx-auto" />
                            <h3 className="text-lg font-medium text-foreground">אין שאלות זמינות</h3>
                            <p className="text-sm text-muted-foreground max-w-md">
                              בקש מהAI שאלות כדי שהוא יוכל להבין טוב יותר את התמלול
                            </p>
                          </div>
                          <Button
                            onClick={handleGenerateQuestions}
                            disabled={isGeneratingQuestions || transcription.processing.status !== "completed"}
                            className="flex items-center gap-2"
                          >
                            {isGeneratingQuestions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                            {isGeneratingQuestions ? "יוצר שאלות..." : "צור שאלות AI"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer Info */}
          <div className="px-6 pb-4 text-xs text-muted-foreground text-right flex-shrink-0">
            נוצר ב-{formatDate(transcription.createdAt)} •
            {transcription.processing.completedAt && ` הושלם ב-${formatDate(transcription.processing.completedAt)}`}
            {transcription.processing.processingTimeMs &&
              ` • זמן עיבוד: ${Math.round(transcription.processing.processingTimeMs / 1000)} שניות`}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptionViewer;
